import React from "react";
import {useTranslation} from "react-i18next";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

import DialogContent from "@mui/material/DialogContent";
import MDButton from "materialDashboard/components/MDButton";
import {useFormik} from "formik";
import Grid from "@mui/material/Grid";
import FormSubSectionTitle from "./FormSubSectionTitle";
import IncidentSelect from "./IncidentSelect";
import GridItemOffset from "./GridItemOffset";
import IncidentField from "./IncidentField";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import Divider from "@mui/material/Divider";
import MDInput from "../../../materialDashboard/components/MDInput";
import {useIncidentAttachmentSchema} from "../../../schemaValidations/incidentAttachmentSchema";
import {FieldWithTooltip} from "./FormTooltip";


const AttachmentDialog = ({isOpen, onClose, onApply, attachment, incidentRegistry}) => {
    const {t} = useTranslation(['incidents', 'enums']);

    const attachmentTypeRegistry = incidentRegistry.data?.attachmentType ?? [];
    const attachmentSourceRegistry = incidentRegistry.data?.attachmentSource ?? [];
    const attachmentVisibilityRegistry = incidentRegistry.data?.attachmentVisibility ?? [];

    const validationSchema = useIncidentAttachmentSchema();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            key: attachment?.key,
            id: attachment?.id,
            attachmentType: attachment?.attachmentType,
            attachmentSource: attachment?.attachmentSource,
            visibility: attachment?.visibility,
            webLink: attachment?.webLink,
            description: attachment?.description,
            file: attachment?.file,
            fileUpload: attachment?.fileUpload,
        },
        validationSchema,
        onSubmit: (values, {resetForm}) => {
            onApply(values);
            resetForm();  // reset form to initial values
        }
    });

    return <Dialog onClose={onClose} open={isOpen} maxWidth="lg" fullWidth>
        <DialogTitle>
            {t('incidentForm.attachmentDialog.title')}
        </DialogTitle>

        <DialogContent>
            <Grid container rowSpacing={2} columnSpacing={4}>
                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.attachmentDialog.attachmentType')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentSelect formik={formik}
                                    name="attachmentType"
                                    registry={attachmentTypeRegistry}/>
                </Grid>
                <GridItemOffset xs={12} md={4}/>
                <Grid item xs={12} md={8}>
                    <IncidentSelect formik={formik}
                                    name="attachmentSource"
                                    registry={attachmentSourceRegistry}/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.attachmentDialog.linkOrFile')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <FieldWithTooltip message={t('incidentForm.tooltip.webLink')}>
                        <IncidentField formik={formik}
                                       name="webLink"
                                       disabled={!!formik.values.fileUpload || !!formik.values.file}
                                       type="url"/>
                    </FieldWithTooltip>
                </Grid>
                <GridItemOffset xs={12} md={4}/>
                <Grid item xs={12} md={8}>
                    <MDTypography variant="body2" color="secondary">
                        { formik.values.file?.filename ?? ""}
                    </MDTypography>
                    <FieldWithTooltip message={t('incidentForm.tooltip.fileUpload')}>
                        <MDInput type="file"
                                 name="fileUpload"
                                 variant="standard"
                                 fullWidth
                                 onChange={ (e) => {
                                     formik.setFieldValue("fileUpload", e.currentTarget.files[0]);
                                 }}
                                 error={formik.touched.fileUpload && Boolean(formik.errors.fileUpload)}
                                 helperText={formik.touched.fileUpload && formik.errors.fileUpload}
                                 disabled={!!formik.values.webLink || !!formik.values.file}
                        />
                    </FieldWithTooltip>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.attachmentDialog.description')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentField formik={formik}
                                   name="description"/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.attachmentDialog.visibility')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentSelect formik={formik}
                                    name="visibility"
                                    registry={attachmentVisibilityRegistry}/>
                </Grid>
            </Grid>

            <Divider />

            <MDTypography variant="body2" fontStyle="italic" color="secondary" textAlign='center' mt={2}>
                {t('incidentForm.attachmentDialog.gdprDisclaimer')}
            </MDTypography>
        </DialogContent>

        <DialogActions>
            <MDButton onClick={onClose}>{t('incidentForm.attachmentDialog.cancel')}</MDButton>
            <MDButton onClick={formik.handleSubmit} color='info'>{t('incidentForm.attachmentDialog.apply')}</MDButton>
        </DialogActions>
    </Dialog>
}

export default AttachmentDialog;